import { useState } from 'react';
import { toast } from 'sonner';
import { X, Paperclip } from 'lucide-react';
import { FadeUp } from './Reveal';
import { api, apiErrorMessage } from '../lib/api';

const DEFAULT = {
  company_name: '',
  contact_name: '',
  email: '',
  country: 'Other',
  phone: '',
  debtor_name: '',
  claim_amount_eur: '',
  invoice_date: '',
  case_summary: '',
  consent: true,
};

const COUNTRIES_OPTIONS = ['UAE', 'USA', 'UK', 'Germany', 'Greece', 'Spain', 'France', 'Switzerland', 'Netherlands', 'Other'];
const MAX_FILES = 5;
const MAX_FILE_MB = 5;

export default function ClaimForm({ presetCountry }) {
  const [form, setForm] = useState({ ...DEFAULT, country: presetCountry || 'Other' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = (e) => {
    const chosen = Array.from(e.target.files || []);
    const combined = [...files, ...chosen].slice(0, MAX_FILES);
    const oversized = combined.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized) {
      toast.error(`"${oversized.name}" exceeds ${MAX_FILE_MB} MB.`);
      return;
    }
    setFiles(combined);
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error('Please confirm the data-processing consent.');
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === 'boolean' ? String(v) : (v ?? '')));
      files.forEach((f) => fd.append('files', f));
      await api.post('/claims', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSent(true);
      toast.success('Your claim has been received. A written response will follow shortly.');
      setForm({ ...DEFAULT, country: presetCountry || 'Other' });
      setFiles([]);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Submission failed. Please retry.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="claim-form" className="relative bg-ink-950 py-24 md:py-40 border-t border-white/10" data-testid="section-claim-form">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5">
          <div className="flex items-baseline gap-4 mb-10">
            <span className="font-serif italic text-white/40 text-5xl font-light leading-none">07</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">— Free preliminary claim assessment</span>
          </div>
          <FadeUp>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1] tracking-tight">
              Submit your file. <br /><em className="not-italic text-white/50">Receive our initial assessment.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.08}>
            <p className="mt-6 font-serif italic text-lg md:text-xl text-white/75 leading-relaxed max-w-md">
              Not sure whether your claim is worth pursuing in Italy? Send us the documents.
              We&apos;ll tell you what we see.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-6 text-white/60 leading-relaxed text-sm md:text-base max-w-md">
              Initial assessment of the claim and its recovery prospects — procedural route,
              expected timing and cost estimate — delivered by email, at no cost, before any engagement.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ul className="mt-8 space-y-2 text-white/50 text-sm">
              <li>— Protected by Italian professional secrecy from receipt (art. 622 c.p.)</li>
              <li>— No obligation to proceed</li>
              <li>— Response typically within 24–72 hours</li>
              <li>— Attach up to 5 documents (invoices, contracts, PDF, max {MAX_FILE_MB} MB each)</li>
            </ul>
          </FadeUp>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <FadeUp delay={0.05}>
            <form onSubmit={submit} className="bg-ink-900 border border-white/10 p-6 md:p-10 space-y-6" data-testid="claim-form">
              {sent && (
                <div className="border border-white/20 p-4 text-white/80 text-sm font-mono uppercase tracking-[0.2em]" data-testid="claim-success">
                  — Received. Reference sent to your email.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Company"><input required data-testid="input-company" value={form.company_name} onChange={(e) => upd('company_name', e.target.value)} className="fld" /></Field>
                <Field label="Contact name"><input required data-testid="input-contact" value={form.contact_name} onChange={(e) => upd('contact_name', e.target.value)} className="fld" /></Field>
                <Field label="Email"><input required type="email" data-testid="input-email" value={form.email} onChange={(e) => upd('email', e.target.value)} className="fld" /></Field>
                <Field label="Phone (optional)"><input data-testid="input-phone" value={form.phone} onChange={(e) => upd('phone', e.target.value)} className="fld" /></Field>
                <Field label="Country of origin">
                  <select data-testid="select-country" value={form.country} onChange={(e) => upd('country', e.target.value)} className="fld">
                    {COUNTRIES_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Italian debtor (name / company)"><input required data-testid="input-debtor" value={form.debtor_name} onChange={(e) => upd('debtor_name', e.target.value)} className="fld" /></Field>
                <Field label="Claim amount (EUR)"><input data-testid="input-amount" placeholder="e.g. 45.000" value={form.claim_amount_eur} onChange={(e) => upd('claim_amount_eur', e.target.value)} className="fld" /></Field>
                <Field label="Invoice / due date"><input data-testid="input-invoice" placeholder="e.g. 2024-11-30" value={form.invoice_date} onChange={(e) => upd('invoice_date', e.target.value)} className="fld" /></Field>
              </div>

              <Field label="Case summary">
                <textarea required rows={6} data-testid="input-summary" value={form.case_summary} onChange={(e) => upd('case_summary', e.target.value)} className="fld resize-none"
                  placeholder="Brief factual timeline, nature of the underlying contract and steps already taken." />
              </Field>

              {/* File upload */}
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-2">— Supporting documents (optional)</span>
                <label
                  htmlFor="claim-files"
                  data-testid="file-upload-trigger"
                  className="inline-flex items-center gap-2 border border-white/15 border-dashed px-4 py-3 text-white/70 text-sm hover:bg-white/5 cursor-pointer transition-colors"
                >
                  <Paperclip className="h-4 w-4" strokeWidth={1.25} />
                  <span>Attach files ({files.length}/{MAX_FILES})</span>
                </label>
                <input
                  id="claim-files"
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={onFiles}
                  data-testid="file-input"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.eml,.txt"
                />
                {files.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid="file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-xs text-white/70 border-b border-white/5 pb-1">
                        <span className="truncate max-w-[75%]">{f.name} · {(f.size / 1024).toFixed(0)} KB</span>
                        <button type="button" onClick={() => removeFile(i)} data-testid={`file-remove-${i}`} className="text-white/40 hover:text-white">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <label className="flex items-start gap-3 text-white/60 text-xs cursor-pointer" data-testid="consent-label">
                <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => upd('consent', e.target.checked)} className="mt-0.5 accent-white" />
                <span>
                  I consent to the processing of the data submitted for the sole purpose of preliminary case
                  evaluation, in accordance with EU Regulation 2016/679 (GDPR). No engagement is created.
                </span>
              </label>

              <p className="text-white/40 text-[11px] leading-relaxed" data-testid="claim-form-notice">
                Submitting this form does not establish a lawyer–client relationship; a conflict-of-interest check
                is carried out before any engagement. Please do not use this form for urgent matters or impending
                deadlines — call +39 045.8011276 instead. See our{' '}
                <a href="/privacy" className="underline underline-offset-4 hover:text-white transition-colors">Privacy Policy</a>.
              </p>

              <div className="flex flex-wrap items-center gap-6 pt-2">
                <button type="submit" disabled={busy} data-testid="submit-claim-button"
                  className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-[11px] font-mono uppercase tracking-[0.25em] hover:bg-oxblood hover:text-white transition-colors duration-300 disabled:opacity-50">
                  {busy ? 'Submitting…' : 'Submit for Review →'}
                </button>
                <span className="font-mono text-[10px] text-white/40 uppercase tracking-[0.25em]">Reply within 48 working hours</span>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>

      <style>{`
        .fld { width:100%; background:transparent; border:none; border-bottom:1px solid rgba(255,255,255,0.15); color:#fff; font-size:15px; padding:10px 0; font-family:'IBM Plex Sans',sans-serif; transition:border-color 300ms; }
        .fld::placeholder { color:rgba(255,255,255,0.3); }
        .fld:focus { outline:none; border-bottom-color:#fff; }
        select.fld { appearance:none; background-image: linear-gradient(45deg, transparent 50%, #fff 50%), linear-gradient(-45deg, transparent 50%, #fff 50%); background-position: calc(100% - 12px) 50%, calc(100% - 6px) 50%; background-size: 6px 6px, 6px 6px; background-repeat: no-repeat; }
        select.fld option { background:#0a0a0a; color:#fff; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-1">— {label}</span>
      {children}
    </label>
  );
}
